// src/services/publicFeedbackService.js
import { db } from '../config/firebase';
import { 
  doc, 
  getDoc, 
  getDocs, 
  collection, 
  addDoc, 
  serverTimestamp,
  query,
  where
} from 'firebase/firestore';

/**
 * Public feedback service for accessing invoice data without tenant context
 * Used by the public feedback form that clients access via email links
 */
class PublicFeedbackService {
  
  /**
   * Get invoice data using fast global registry lookup (used for public feedback)
   * This is safe because we validate the security token
   */
  async getInvoiceByNumber(invoiceNumber) {
    try {
      console.log('🔍 Fast lookup for invoice number:', invoiceNumber);
      
      // First, check global registry for fast lookup
      const registryRef = doc(db, 'global_invoice_registry', invoiceNumber);
      const registrySnap = await getDoc(registryRef);
      
      if (registrySnap.exists()) {
        const registryData = registrySnap.data();
        console.log('🚀 Fast lookup found invoice in tenant:', registryData.tenantId);
        
        // Get the actual invoice from the tenant
        const invoiceRef = doc(db, `tenants/${registryData.tenantId}/invoices`, registryData.invoiceId);
        const invoiceSnap = await getDoc(invoiceRef);
        
        if (invoiceSnap.exists()) {
          const invoiceData = {
            id: invoiceSnap.id,
            tenantId: registryData.tenantId,
            ...invoiceSnap.data()
          };
          console.log('✅ Found invoice via fast lookup:', invoiceData);
          return invoiceData;
        }
      }
      
      console.log('❌ Invoice not found in global registry, falling back to tenant scan');
      
      // Fallback to old method if registry lookup fails
      return await this.getInvoiceByNumberFallback(invoiceNumber);
    } catch (error) {
      console.error('💥 Error in fast invoice lookup:', error);
      throw new Error(`Failed to find invoice: ${error.message}`);
    }
  }

  /**
   * Fallback method - scan all tenants (kept for compatibility)
   */
  async getInvoiceByNumberFallback(invoiceNumber) {
    try {
      console.log('🔄 Fallback: Scanning all tenants for invoice:', invoiceNumber);
      
      const tenantsSnapshot = await getDocs(collection(db, 'tenants'));
      console.log('📁 Found tenants:', tenantsSnapshot.docs.length);
      
      for (const tenantDoc of tenantsSnapshot.docs) {
        const tenantId = tenantDoc.id;
        console.log(`🏥 Checking tenant: ${tenantId}`);
        
        try {
          const invoicesRef = collection(db, `tenants/${tenantId}/invoices`);
          const q = query(invoicesRef, where('invoiceNumber', '==', invoiceNumber));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const invoiceDoc = querySnapshot.docs[0];
            const invoiceData = {
              id: invoiceDoc.id,
              tenantId: tenantId,
              ...invoiceDoc.data()
            };
            console.log('✅ Found invoice via fallback:', invoiceData);
            return invoiceData;
          }
        } catch (error) {
          console.warn(`❌ Failed to check tenant ${tenantId}:`, error);
          continue;
        }
      }
      
      console.log('❌ Invoice not found in any tenant');
      return null;
    } catch (error) {
      console.error('💥 Error in fallback search:', error);
      throw new Error(`Failed to find invoice: ${error.message}`);
    }
  }

  /**
   * Get client data from the same tenant as the invoice
   */
  async getClientById(tenantId, clientId) {
    try {
      const clientRef = doc(db, `tenants/${tenantId}/clients`, clientId);
      const clientSnap = await getDoc(clientRef);
      
      if (clientSnap.exists()) {
        return {
          id: clientSnap.id,
          ...clientSnap.data()
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting client:', error);
      throw new Error(`Failed to get client: ${error.message}`);
    }
  }

  /**
   * Get appointment data from the same tenant as the invoice
   */
  async getAppointmentById(tenantId, appointmentId) {
    try {
      const appointmentRef = doc(db, `tenants/${tenantId}/appointments`, appointmentId);
      const appointmentSnap = await getDoc(appointmentRef);
      
      if (appointmentSnap.exists()) {
        return {
          id: appointmentSnap.id,
          ...appointmentSnap.data()
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting appointment:', error);
      throw new Error(`Failed to get appointment: ${error.message}`);
    }
  }

  /**
   * Check if feedback already exists for this invoice number
   */
  async checkExistingFeedback(tenantId, invoiceNumber) {
    try {
      const feedbackRef = collection(db, `tenants/${tenantId}/feedback`);
      const q = query(feedbackRef, where('invoiceNumber', '==', invoiceNumber));
      const querySnapshot = await getDocs(q);
      
      return !querySnapshot.empty;
    } catch (error) {
      console.error('Error checking existing feedback:', error);
      return false; // Allow submission if check fails
    }
  }

  /**
   * Submit feedback for an invoice
   */
  async submitFeedback(tenantId, feedbackData) {
    try {
      const feedbackRef = collection(db, `tenants/${tenantId}/feedback`);
      const docRef = await addDoc(feedbackRef, {
        ...feedbackData,
        submittedAt: new Date(),
        createdAt: new Date()
      });
      
      return {
        id: docRef.id,
        ...feedbackData
      };
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw new Error(`Failed to submit feedback: ${error.message}`);
    }
  }

  /**
   * Validate security token (basic validation)
   */
  validateToken(token, invoiceId, clientEmail) {
    try {
      const decoded = atob(token);
      
      // Check if token contains invoice ID and appears to be valid format
      if (!decoded.includes(invoiceId)) {
        return false;
      }
      
      // Additional validation could be added here
      // For now, we do basic format checking
      const parts = decoded.split(':');
      return parts.length >= 2; // Should have email:invoiceId:timestamp format
      
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }
}

const publicFeedbackService = new PublicFeedbackService();
export { publicFeedbackService };
export default publicFeedbackService;
